/**
 * Service-book links on a vehicle card (#2201).
 *
 * Shows the make's manufacturer book when this vehicle's year is in range,
 * then the workshop's tenant-level bookmarks so those stay available too.
 */

import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useTranslation } from 'react-i18next';
import { useVehicleMakes } from '@/hooks/useVehicles';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import {
  findMakeEntry,
  formatServiceBookYearRange,
  isMakeServiceBookRelevant,
} from './vehicleServiceBook';

interface VehicleServiceBookLinksProps {
  make: string | null | undefined;
  year: number | null | undefined;
}

export function VehicleServiceBookLinks({ make, year }: VehicleServiceBookLinksProps) {
  const { t } = useTranslation('vehicles');
  const { data: makes } = useVehicleMakes();
  const { serviceBookLinks } = useTenantSettings();

  const entry = findMakeEntry(makes, make);
  const settings = entry?.settings ?? null;
  const brandRelevant = isMakeServiceBookRelevant(settings, year);
  const brandUrl = brandRelevant ? settings?.serviceBookUrl : null;
  const yearRange =
    settings == null
      ? null
      : formatServiceBookYearRange(settings.serviceBookYearFrom, settings.serviceBookYearTo);

  if (brandUrl == null && serviceBookLinks.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <MenuBookIcon color="action" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={600}>
          {t('serviceBook.cardTitle')}
        </Typography>
      </Box>
      <Stack spacing={0.75}>
        {brandUrl != null && (
          <Box>
            <Link href={brandUrl} target="_blank" rel="noopener noreferrer" underline="hover">
              {settings?.serviceBookName || t('makes.hasServiceBook')}
            </Link>
            {yearRange != null && (
              <Typography variant="caption" color="text.secondary" display="block">
                {t('serviceBook.yearRange', { years: yearRange })}
              </Typography>
            )}
          </Box>
        )}
        {serviceBookLinks.map((link, index) => (
          <Box key={`${link.url}-${String(index)}`}>
            <Link href={link.url} target="_blank" rel="noopener noreferrer" underline="hover">
              {link.name}
            </Link>
            {link.note !== undefined && link.note !== '' && (
              <Typography variant="caption" color="text.secondary" display="block">
                {link.note}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
